import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import "./Survey.css";

function TakeSurvey({ isDarkMode }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Survey data states
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Response text + loading/error states
  const [responseText, setResponseText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // reCAPTCHA token
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  // Fetch the survey on mount
  useEffect(() => {
    // 1) Check if user already responded on this device
    const hasResponded = localStorage.getItem(`responded_${slug}`);
    if (hasResponded === "true") {
      setError("You have already responded to this survey.");
      setLoading(false);
      return;
    }

    // 2) Fetch the survey from the server
    const fetchSurvey = async () => {
      try {
        const res = await fetch(`/public/${slug}`);
        if (!res.ok) {
          setError("Survey not found");
          setLoading(false);
          return;
        }
        const data = await res.json();

        // If the survey is closed => show error
        if (data.closed) {
          setError("Survey is closed.");
          setTitle(data.title || "Survey");
          if (data.company_name) {
            setCompanyName(data.company_name);
          }
          setLoading(false);
        } else if (data.survey) {
          // Normal (open) survey
          setTitle(data.survey.title);
          setPrompt(data.survey.prompt);
          if (data.company_name) {
            setCompanyName(data.company_name);
          }
          setLoading(false);
        } else {
          setError("Survey not found");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading public survey:", err);
        setError("An error occurred loading the survey.");
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [slug]);

  // reCAPTCHA onChange
  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Submit the response
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!responseText) {
      alert("Please enter a response (max 2500 chars).");
      return;
    }
    
    // **Minimum length check**:
    if (responseText.length < 30) {
      alert("Please enter at least 30 characters in your response.");
      return;
    }

    // Ensure user completed reCAPTCHA
    if (!recaptchaToken) {
      alert("Please complete the reCAPTCHA first.");
      return;
    }

    try {
      const res = await fetch(`/public/${slug}/response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          responseText,
          recaptchaToken, // send token to server
        }),
      });
      if (res.ok) {
        // Mark responded in localStorage
        localStorage.setItem(`responded_${slug}`, "true");
        // Redirect to thank-you page
        navigate("/thank-you");
      } else {
        const err = await res.json();
        alert(err.error || "Error submitting response");
      }
    } catch (error) {
      console.error("Error submitting response:", error);
      alert("An error occurred. Please try again.");
    }
  };

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading survey...</p>;
  }

  // If there's an error, show it
  if (error) {
    return (
      <div className="survey-container">
        <h2>{title || "Survey"}</h2>
        {companyName && (
          <p style={{ fontStyle: "italic", marginBottom: "0.5rem" }}>
            Company: {companyName}
          </p>
        )}
        <p style={{ color: "red", textAlign: "center" }}>{error}</p>
      </div>
    );
  }

  // Otherwise, show the form
  return (
    <div className="survey-container" style={{ position: "relative" }}>
      <h1 className="takesurvey-title">{title}</h1>
      <p style={{ textAlign: "center" }}>
        <strong>Your answers are always anonymous.</strong>
      </p>
      {companyName && (
        <p style={{ fontStyle: "italic", marginBottom: "1rem" }}>
          Company: {companyName}
        </p>
      )}
      <p style={{ marginBottom: "1rem" }}>
        <strong>Feedback Topic:</strong> {prompt}
      </p>

      <form onSubmit={handleSubmit}>
        <label
          htmlFor="surveyResponse"
          style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600 }}
        >
          Your Response (max 2500 chars):
        </label>
        <textarea
          id="surveyResponse"
          rows={8}
          value={responseText}
          onChange={(e) => {
            if (e.target.value.length <= 2500) {
              setResponseText(e.target.value);
            }
          }}
          placeholder="Enter your response here..."
          className="take-survey-textarea"
        />
        <div className="char-count">{responseText.length} / 2500</div>

        {/* reCAPTCHA with dynamic theme */}
        <ReCAPTCHA
          key={isDarkMode ? "dark-recaptcha" : "light-recaptcha"}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={onRecaptchaChange}
          theme={isDarkMode ? "dark" : "light"}
        />

        <button
          type="submit"
          className="logout-btn"
          style={{ marginTop: "10px" }}
        >
          Submit
        </button>
      </form>
    </div>
  );
}

export default TakeSurvey;
